import React, {FC, useEffect, useRef, useState} from 'react';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Banner, BannerTypesEnum} from '../../../../../libs/shared/ui/src/components/banners/banner';
import {BannerCloseAction} from '../../../../../libs/shared/ui/src/components/banners/bannerCloseAction';
import {BannerIcon} from '../../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerProgress} from '../../../../../libs/shared/ui/src/components/banners/bannerProgress';
import {BannerPrioritiesEnum} from '../../../../../libs/shared/ui/src/components/banners/bannersContext';
import {BannerSecondaryText} from '../../../../../libs/shared/ui/src/components/banners/bannerSecondaryText';
import {BannerText} from '../../../../../libs/shared/ui/src/components/banners/bannerText';
import {UserThemeConsumer} from '../../../../../libs/shared/ui/src/theme/userThemeConsumer';

/*
 * Props.
 */

interface ChannelImportBannerProps {
  /** A fraction indicating the progress of the import. */
  progress: number;
}

/*
 * Component.
 */

export const ChannelImportBanner: FC<ChannelImportBannerProps> = (props) => {
  const {progress} = props;
  const isImporting = progress < 1;
  const previousIsImportingRef = useRef(isImporting);
  const hasImportingChanged = isImporting !== previousIsImportingRef.current;
  const [shouldDisplaySuccess, setShouldDisplaySuccess] = useState(false);

  useEffect(() => {
    // We're only interested in state transitions.
    if (isImporting === previousIsImportingRef.current) {
      return;
    }

    previousIsImportingRef.current = isImporting;

    // Display the success banner if we're done importing.
    setShouldDisplaySuccess(!isImporting);
  }, [isImporting]);

  const onDismissClick = () => {
    setShouldDisplaySuccess(false);
  };

  // If nothing is going on, no need to display a banner at all.
  if (!isImporting && !shouldDisplaySuccess && !hasImportingChanged) {
    return null;
  }

  // Otherwise, check if the import is still in progress or if it has just completed.
  return isImporting ? renderProgressBanner(progress) : renderCompletedBanner(onDismissClick);
};

function renderProgressBanner(progress: number) {
  return (
    <Banner bannerType={BannerTypesEnum.INLINE} priority={BannerPrioritiesEnum.INFORMATION}>
      <BannerProgress progress={progress} />
      <BannerIcon iconName="repeatCircleSmall" shouldRotate />
      <BannerText>
        <Trans id="banner-channelimport-importing">Importing your emails and more</Trans>
      </BannerText>
      <BannerSecondaryText>
        <Trans id="banner-channelimport-progress">{Math.floor(100 * progress)}%</Trans>
      </BannerSecondaryText>
    </Banner>
  );
}

function renderCompletedBanner(onDismissClick: () => void) {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <Banner
          bannerType={BannerTypesEnum.INLINE}
          priority={BannerPrioritiesEnum.INFORMATION}
          color={theme.banner.successBackground}
        >
          <BannerCloseAction onClick={onDismissClick} />
          <BannerIcon iconName="checkmarkCircleSmall" />
          <BannerText>
            <Trans id="banner-channelimport-complete">Import complete</Trans>
          </BannerText>
        </Banner>
      )}
    </UserThemeConsumer>
  );
}
